import type { InvestmentActions, InvestmentNote } from "@eu.mdotm.api/sphere";
import { formatDate } from "$root/localization/formatters";
import { ActionText, Icon, Text } from "@mdotm/shadcn/legacy/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor, invertSortFn } from "@mdotm/shadcn/legacy/utils";
import { Map } from "immutable";
import { type MouseEvent, type MouseEventHandler, useEffect, useRef, useState } from "react";

const typeToStringMap: Record<InvestmentActions, { title: string; downloadText?: string }> = {
	UPLOAD: { title: "Upload", downloadText: "Composition" },
	CREATION: { title: "Creation", downloadText: "Composition" },
	ENHANCEMENT: { title: "Proposal accepted", downloadText: "Composition & trades" },
	OPTIMIZATION: { title: "Optimization accepted", downloadText: "Composition & trades" },
	EDIT: { title: "Composition edited ", downloadText: "Composition & trades" },
	EVOLVE: { title: "Portfolio evolved", downloadText: "Composition & trades" },
	RETRIEVING_DATA_FOR_UPLOAD: { title: "Retrieving data", downloadText: "Composition & trades" },
	CLONE: { title: "Portfolio cloned", downloadText: "Composition & trades" },
	EDIT_ENHANCEMENT_PROXY_LIVE_INVESTMENT: {
		title: "Instrument risk model edited",
		downloadText: "Composition & trades",
	},
	EDIT_PROXY_LIVE_INVESTMENT: { title: "Instrument risk model edited", downloadText: "Composition & trades" },
	ENHANCEMENT_FROM_MARKET_VIEW: { title: "Proposal accepted", downloadText: "Composition & trades" },
	EDIT_WITHOUT_ACCEPT: { title: "Composition edited", downloadText: "Composition & trades" },
	EDIT_BENCHMARKS: { title: "Benchmark edited", downloadText: "Composition & trades" },
	EDIT_STATIC_CONFIGURATION: { title: "Edit portfolio settings", downloadText: "Composition & trades" },
	DELETE: { title: "Delete", downloadText: "Composition & trades" },
	REJECT_ENHANCEMENT: { title: "Reject enhancement", downloadText: "Composition & trades" },
	ACCEPT_ENHANCEMENT: { title: "Accept enhancement", downloadText: "Composition & trades" },
	ALLOCATION_CONSTRAINTS_CHECK: { title: "Allocation constraint check", downloadText: "-" }, // not user trackable action
	RISK_CONSTRAINT_CHECK: { title: "Risk constraint check", downloadText: "-" }, // not user trackable action
	BUILD_AND_SEND_INVESTMENT_REPORTS: { title: "Risk constraint check", downloadText: "-" }, // not user trackable action
	EDIT_AT_DATE: { title: "Edit at date", downloadText: "-" },
	IMPORT_DAILY_RETURNS: { title: "Import daily returns", downloadText: "-" },
	BUILD_AND_SEND_INVESTMENT_REPORTS_AT_FIXED_DATE: {
		title: "Build and send investment reports at fixed date",
		downloadText: "-",
	},
	REAL_SHARES_TRANSFORM: { title: "Convert to quantities", downloadText: "Composition & trades" },
	PORTFOLIO_LAB: { title: "Portfolio Lab", downloadText: "Composition & trades" },
};

export type MockMacroComposition = {
	firstQualityLevel?: string;
	weight?: number;
};

type CompositionTooltipProps = {
	title?: string;
	subtitle?: string;
	subtitleClassName?: HTMLParagraphElement["className"];
	composition: Array<MockMacroComposition>;
	compared?: boolean;
	note?: InvestmentNote;
	action?: InvestmentActions;
} & (
	| {
			onRemove?: MouseEventHandler<HTMLButtonElement>;
			onCompare?: undefined;
	  }
	| {
			onCompare?: MouseEventHandler<"button"> & ((e: MouseEvent<Element, globalThis.MouseEvent>) => void);
			onRemove?: undefined;
	  }
);

const exposureColorMap = Map([
	["EQUITY", "#33c8ff"],
	["FIXED-INCOME", "#ffd866"],
	["FIXED_INCOME", "#ffd866"],
	["COMMODITIES", "#f69b6f"],
	["ALTERNATIVE", "#dd8596"],
	["MONEY-MARKET", "#8c99d9"],

	["Equity", "#33c8ff"],
	["Fixed Income", "#ffd866"],
	["Commodities", "#f69b6f"],
	["Alternative", "#dd8596"],
	["Money Market", "#8c99d9"],
]);

function PortfolioExposureSummary(props: CompositionTooltipProps): React.JSX.Element {
	const [isClamped, setIsClamped] = useState(false);
	const [expand, setExpand] = useState(false);
	const { composition, title, subtitle, compared, subtitleClassName } = props;
	const descriptionRef = useRef<HTMLParagraphElement>(null);
	const ascCompositionSorted = composition.toSorted(invertSortFn(builtInSortFnFor("weight")));

	useEffect(() => {
		if (descriptionRef?.current) {
			setIsClamped(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
		}
	}, []);

	return (
		<>
			<div className="mb-2 flex justify-between items-start">
				<div
					className={toClassName({
						"max-w-[calc(100%-70px)]": props.onCompare !== undefined,
						"max-w-[calc(100%-20px)]": props.onRemove !== undefined,
					})}
				>
					<Text as="p" type="Body/L/Bold" className="truncate text-ellipsis" title={title}>
						{title}
					</Text>
					{subtitle && (
						<Text
							as="p"
							type="Body/M/Book"
							className={toClassName({
								"truncate text-ellipsis": true,
								[subtitleClassName ?? ""]: subtitleClassName !== undefined,
							})}
							title={subtitle}
						>
							{subtitle}
						</Text>
					)}
				</div>
				<div className="shrink-0 flex">
					{props.onCompare && (
						<ActionText as="button" onClick={props.onCompare} color={themeCSSVars.palette_P400}>
							<Text as="span" type="Body/L/Bold" color={themeCSSVars.palette_P400}>
								{compared ? "Remove" : "Compare"}
							</Text>
						</ActionText>
					)}
					{props.onRemove && (
						<button type="button" className="" onClick={props.onRemove}>
							<Icon icon="Close" size={16} color={themeCSSVars.palette_N400} />
						</button>
					)}
				</div>
			</div>
			<hr
				className={toClassName({
					[`border-dashed border-[width:1px] border-[color:var(--palette-N200)]`]: true,
					"mb-4": Boolean(props.note?.text) === false,
					"mb-1": Boolean(props.note?.text),
				})}
			/>
			{props.note?.text && props.action && (
				<article className="mb-2">
					<Text as="p" type="Body/M/Bold" className="truncate pr-2">
						Last status: {typeToStringMap[props.action].title}&nbsp;
						<Text as="span" type="Body/S/Book">
							{formatDate(props.note.date)}
						</Text>
					</Text>
					<p
						ref={descriptionRef}
						className={toClassName({
							"line-clamp-2": expand === false,
							"line-clamp-none": expand,
						})}
					>
						{props.note?.text}
						{isClamped && (
							<ActionText as="span" onClick={() => setExpand((isExpanded) => !isExpanded)}>
								{isClamped ? "See more" : "Hide"}
							</ActionText>
						)}
					</p>

					{/* <hr className={`border-dotted border-[width:1px] border-[color:var(--palette-N200)]`} /> */}
				</article>
			)}
			<div className="flex space-x-1 w-full mt-auto">
				<ForEach collection={ascCompositionSorted}>
					{(item) => (
						<div
							className="overflow-hidden py-1 flex-1 text-center"
							style={{
								backgroundColor: exposureColorMap.get(item.firstQualityLevel ?? "") ?? "",
								width: `${item.weight}%`,
							}}
						>
							<Text
								as="p"
								type="Body/S/Book"
								className="px-1 text-center truncate"
								title={item.firstQualityLevel ?? "_"}
							>
								{item.firstQualityLevel ?? "_"}
							</Text>
							<Text as="p" type="Body/S/Bold" className="text-center">
								{item.weight ?? 0}%
							</Text>
						</div>
					)}
				</ForEach>
			</div>
		</>
	);
}

export default PortfolioExposureSummary;
